// actions/mapBounds.ts
import { get } from 'svelte/store'
import type { MapViewBlockFieldsFragment } from '$lib/queries/fragments/generated/MapViewBlockFields'
import { mapStore } from '$lib/stores/map-state'
import { urlState } from '$lib/stores/url-state'
import blockState from '$lib/stores/block-state'
import { BlockType } from '$src/lib/graphql/enums'
import type { MapState } from '$lib/utils/map'
import { browser } from '$app/environment'
import { tick } from 'svelte'

export function areBoundsEqual(
	currentBounds: google.maps.LatLngBounds,
	newBounds: google.maps.LatLngBounds,
): boolean {
	const b1 = currentBounds.toJSON()
	const b2 = newBounds.toJSON()

	const res =
		Math.abs(b1.north - b2.north) < 0.000001 &&
		Math.abs(b1.south - b2.south) < 0.000001 &&
		Math.abs(b1.east - b2.east) < 0.000001 &&
		Math.abs(b1.west - b2.west) < 0.000001

	return res
}

export async function fitBounds(
	blocks: MapViewBlockFieldsFragment[],
	placeSearchResults: any[],
	isMobileScreen: boolean,
	currentBounds: google.maps.LatLngBounds | null,
	requirementsResults: MapViewBlockFieldsFragment[],
): Promise<google.maps.LatLngBounds | null> {
	const { map, isLoaded } = get(mapStore)
	if (!map || !isLoaded) return

	if (get(blockState).recenterMap === false || get(blockState).searchSelected != null) return

	if (browser) await tick()

	const newBounds = new google.maps.LatLngBounds()
	const mapState = get(blockState)?.mapState ?? null
	// console.log('fitBounds', mapState)

	// Wait for map projection to be ready
	const maxAttempts = 20
	let attempts = 0
	while (!map.getProjection() && attempts < maxAttempts) {
		await new Promise((resolve) => setTimeout(resolve, 50))
		attempts++
	}

	if (attempts >= maxAttempts) {
		console.warn('Map projection not ready after maximum attempts')
		return newBounds
	}

	switch (mapState as MapState) {
		case 'search':
			placeSearchResults?.forEach((place) => {
				newBounds.extend({ lat: place?.lat, lng: place?.lng })
			})
			break

		case 'destination':
			await handleDestinationBounds(newBounds, blocks)
			break

		case 'trip':
			const returnSignal = await handleTripBounds(newBounds, blocks)
			if (returnSignal) return returnSignal
			break

		case 'trips':
			const destinationBlocks = blocks.filter(
				(b) => b.blockType === BlockType.Destination && !b.isOrigin && !b.isDestination,
			)
			if (destinationBlocks.length === 0) {
				const res = setDefaultBounds(get(mapStore).map)
				blockState.setRecenterMap(false)
				return res
			}
			destinationBlocks.forEach((b) => {
				if (b.point?.lat && b.point?.lng) {
					newBounds.extend({ lat: b.point.lat, lng: b.point.lng })
				}
			})
			break

		case 'block':
			await handleBlockBounds(newBounds, blocks)
			break

		case 'requirements':
			handleRequirementsBounds(newBounds, requirementsResults)
			break

		case 'custom':
			return

		default:
			const res = setDefaultBounds(get(mapStore).map)
			blockState.setRecenterMap(false)
			return res
	}

	if (!newBounds.isEmpty()) {
		const currentZoom = map.getZoom()

		// Check if bounds have actually changed
		if (currentBounds && areBoundsEqual(currentBounds, newBounds)) {
			blockState.setRecenterMap(false)
			return newBounds
		}

		if (mapState === 'block' && get(urlState).isHighlights) {
			await handleHighlightsBounds(newBounds, map, currentBounds, isMobileScreen)
		} else if (mapState === 'block') {
			await handleRegularBlockBounds(newBounds, map, currentBounds, isMobileScreen)
		} else {
			map.fitBounds(newBounds, isMobileScreen ? 24 : 220)
		}

		await handleZoomAdjustment(map, newBounds, currentBounds, currentZoom)

		if (map && isLoaded) {
			blockState.setRecenterMap(false)
			return newBounds
		}
	} else {
		setDefaultBounds(map)
	}
	return null
}

export function handleRequirementsBounds(
	bounds: google.maps.LatLngBounds,
	requirementsResults: MapViewBlockFieldsFragment[],
) {
	requirementsResults.forEach((block) => {
		bounds.extend({ lat: block.point.lat, lng: block.point.lng })
	})
}

export function setDefaultBounds(map: google.maps.Map): google.maps.LatLngBounds {
	// console.log('Default bounds')
	const isLoaded = get(mapStore).isLoaded

	const bounds = new google.maps.LatLngBounds()
	bounds.extend({ lat: 30.267153, lng: 97.743061 }) // Austin, TX
	// bounds.extend({ lat: 37.774929, lng: -122.419418 }) // San Francisco, CA
	// bounds.extend({ lat: 40.712776, lng: -74.005974 }) // New York, NY

	if (map && isLoaded && browser) {
		map.fitBounds(bounds, 220)

		map.setZoom(2)
		return bounds
	}
	return null
}

async function handleDestinationBounds(
	bounds: google.maps.LatLngBounds,
	blocks: MapViewBlockFieldsFragment[],
) {
	let parentBlockId = get(urlState).parentBlockId
	const currentDestination = blocks.find((block) => block.id === parentBlockId)

	if (!currentDestination) return

	if (currentDestination.point) {
		bounds.extend({
			lat: currentDestination.point.lat,
			lng: currentDestination.point.lng,
		})
	}

	let selectedBlocks: MapViewBlockFieldsFragment[] = []

	switch (currentDestination.blockType) {
		case BlockType.Destination:
			selectedBlocks = blocks.filter((b) => b.parentDestination?.id === currentDestination.id)
			break
		case BlockType.Day:
			selectedBlocks = blocks.filter(
				(b) =>
					b.parentDayId === currentDestination.id &&
					(b.point != null || b.blockType === BlockType.CustomRegion),
			)
			break
		case BlockType.List:
			selectedBlocks = blocks.filter(
				(b) =>
					b.parentListId === currentDestination.id &&
					(b.point != null || b.blockType === BlockType.CustomRegion),
			)
			break
		case BlockType.Source:
			selectedBlocks = blocks.filter(
				(b) =>
					b.parentDestination?.id === currentDestination.parentDestination?.id &&
					(b.point != null || b.blockType === BlockType.CustomRegion),
			)
			break
	}

	extendBoundsWithBlocks(bounds, selectedBlocks)
}

async function handleTripBounds(
	bounds: google.maps.LatLngBounds,
	blocks: MapViewBlockFieldsFragment[],
) {
	const destinationBlocks = blocks.filter(
		(b) => b.blockType === BlockType.Destination && !b.isOrigin && !b.isDestination,
	)

	if (destinationBlocks.length === 0) {
		const res = setDefaultBounds(get(mapStore).map)

		blockState.setRecenterMap(false)
		return res
	}

	destinationBlocks.forEach((b) => {
		if (b.point?.lat && b.point?.lng) {
			bounds.extend({ lat: b.point.lat, lng: b.point.lng })
		}
	})
}

async function handleBlockBounds(
	bounds: google.maps.LatLngBounds,
	blocks: MapViewBlockFieldsFragment[],
) {
	let { blockId, parentBlockId, expandedBlockId } = get(urlState)
	let selectedBlock = blocks.find((block) => block.id === blockId)
	let parentBlock = blocks.find((b) => b.id === selectedBlock?.parentId)

	if (expandedBlockId) {
		parentBlock = blocks?.find((b) => b.id === expandedBlockId)
	} else if (parentBlockId) {
		parentBlock = blocks.find((b) => b.id === parentBlockId)
	}

	if (parentBlock) {
		extendBoundsWithParentBlock(bounds, parentBlock, blocks)
	} else if (selectedBlock) {
		extendBoundsWithSelectedBlock(bounds, selectedBlock, blocks)
	}
}

async function handleHighlightsBounds(
	bounds: google.maps.LatLngBounds,
	map: google.maps.Map,
	currentBounds: google.maps.LatLngBounds | null | undefined,
	isMobileScreen: boolean,
) {
	if (
		!currentBounds?.contains(bounds.getNorthEast()) ||
		!currentBounds?.contains(bounds.getSouthWest()) ||
		map.getZoom() < 12 ||
		!map.getZoom()
	) {
		if (isMobileScreen) {
			map.fitBounds(bounds, 24)
			setTimeout(() => {
				map.panBy(0, 150)
			}, 300)
		} else {
			map.fitBounds(bounds, 220)
		}
	}
}

async function handleRegularBlockBounds(
	bounds: google.maps.LatLngBounds,
	map: google.maps.Map,
	currentBounds: google.maps.LatLngBounds | null | undefined,
	isMobileScreen: boolean,
) {
	if (
		!currentBounds?.contains(bounds.getNorthEast()) ||
		!currentBounds?.contains(bounds.getSouthWest()) ||
		map.getZoom() < 12 ||
		!map.getZoom()
	) {
		map.fitBounds(bounds, isMobileScreen ? 24 : 220)
	}
}

async function handleZoomAdjustment(
	map: google.maps.Map,
	bounds: google.maps.LatLngBounds,
	currentBounds: google.maps.LatLngBounds | null | undefined,
	currentZoom: number | undefined,
) {
	if (
		map.getZoom() > 17 &&
		((bounds.toJSON().south === bounds.toJSON().north &&
			bounds.toJSON().west === bounds.toJSON().east) ||
			(currentBounds?.contains(bounds.getNorthEast()) &&
				currentBounds?.contains(bounds.getSouthWest()) &&
				currentZoom < 12) ||
			(!currentBounds?.contains(bounds.getNorthEast()) &&
				!currentBounds?.contains(bounds.getSouthWest())))
	) {
		setTimeout(() => {
			map.setZoom(12)
		}, 100)
	}
}

function extendBoundsWithBlocks(
	bounds: google.maps.LatLngBounds,
	blocks: MapViewBlockFieldsFragment[],
) {
	blocks.forEach((b) => {
		if (b.point?.lat && b.point?.lng) {
			bounds.extend({ lat: b.point.lat, lng: b.point.lng })
		} else if (b.blockType === BlockType.CustomRegion) {
			b.customRegion?.markers?.forEach((marker) => {
				bounds.extend(marker)
			})
		}
	})
}

function extendBoundsWithParentBlock(
	bounds: google.maps.LatLngBounds,
	parentBlock: MapViewBlockFieldsFragment,
	blocks: MapViewBlockFieldsFragment[],
) {
	if (parentBlock.blockType === BlockType.Day) {
		parentBlock = blocks.find((b) => b.id === parentBlock.parentDestination?.id) || parentBlock
	}

	if (parentBlock.point) {
		bounds.extend({
			lat: parentBlock.point.lat,
			lng: parentBlock.point.lng,
		})
	}

	if (parentBlock.customRegion) {
		parentBlock.customRegion.markers?.forEach((marker) => {
			bounds.extend({ lat: marker.lat, lng: marker.lng })
		})
	}

	blocks
		.filter((b) => b.parentId === parentBlock.id)
		.forEach((b) => {
			if (b.point?.lat && b.point?.lng) {
				bounds.extend({ lat: b.point.lat, lng: b.point.lng })
			} else if (b.blockType === BlockType.CustomRegion) {
				b.customRegion?.markers?.forEach((marker) => {
					bounds.extend({ lat: marker.lat, lng: marker.lng })
				})
			}
		})
}

function extendBoundsWithSelectedBlock(
	bounds: google.maps.LatLngBounds,
	selectedBlock: MapViewBlockFieldsFragment,
	blocks: MapViewBlockFieldsFragment[],
) {
	if (selectedBlock.point) {
		bounds.extend({
			lat: selectedBlock.point.lat,
			lng: selectedBlock.point.lng,
		})
	}

	if (selectedBlock.customRegion?.markers) {
		selectedBlock.customRegion.markers.forEach((marker) => {
			bounds.extend(marker)
		})
	}

	// Handle specific block types
	if (
		[BlockType.Destination, BlockType.Day, BlockType.List, BlockType.Source].includes(
			selectedBlock.blockType as BlockType,
		)
	) {
		let selectedBlocks: MapViewBlockFieldsFragment[] = []

		switch (selectedBlock.blockType) {
			case BlockType.Destination:
				selectedBlocks = blocks.filter((b) => b.parentDestination?.id === selectedBlock.id)
				break
			case BlockType.Day:
				selectedBlocks = blocks.filter(
					(b) =>
						b.parentDayId === selectedBlock.id &&
						(b.point != null || b.blockType === BlockType.CustomRegion),
				)
				break
			case BlockType.List:
				selectedBlocks = blocks.filter(
					(b) =>
						b.parentListId === selectedBlock.id &&
						(b.point != null || b.blockType === BlockType.CustomRegion),
				)
				break
			case BlockType.Source:
				selectedBlocks = blocks.filter(
					(b) =>
						b.parentDestination?.id === selectedBlock.parentDestination?.id &&
						(b.point != null || b.blockType === BlockType.CustomRegion),
				)
				break
		}

		extendBoundsWithBlocks(bounds, selectedBlocks)
	}
}
